import { RegistryWorkspace, RegistryTableSurface } from '@workspace/silk-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  CommerceCatalogWorkspace,
  CommerceObjectSelector,
  HorizontalStack,
  PropertyGrid,
  PropertyItem,
  RegistryEmptyState,
  SilkInput,
  SilkSelect,
  Surface,
  VerticalStack,
  WizardWorkspace,
  type WizardTab,
  SectionHeading,
  Field,
  inputStyles,
  CurrencyInput,
  PercentageInput,
  type CommerceMetadataEntryViewModel,
  type CommerceMetadataValueType,
  DataTable,
  DataTableFacetedFilter,
  DataTableToolbar,
  Checkbox,
  DescriptionText,
  RegistryStatus,
  toast,
  type ColumnDef,
  type ColumnFiltersState,
  type RowSelectionState,
  type SortingState,
} from '@workspace/silk-ui';
import { useScope } from '@workspace/scope-context';
import { FileText, Plus, Save, Trash2, Settings2, Activity, Target, Pencil } from 'lucide-react';
import { useNavigation } from '@/context/NavigationContext';
import { useCommerceRegistryQuery } from '@/hooks/useCommerceRegistryQuery';
import { defineCommerceRegistryColumns } from '@/lib/commerceRegistryColumns';
import {
  formatMinorCurrency,
  formatMinorCurrencyInput,
  parseCurrencyInputToMinor,
} from '@/features/commerce-admin/currency';

const COMMISSION_PLAN_SORT_FIELDS = ['created_at', 'currency', 'name', 'selling_tenant_id', 'status', 'updated_at'] as const;
import { lastPageIndexForTotal, toLimitOffset } from '@/lib/routePagination';
import {
  createCommissionPlan,
  deleteCommissionPlan,
  getCommerceErrorMessage,
  getCommerceErrorStatus,
  listCommissionPlans,
  type CommissionPlan,
  updateCommissionPlan,
} from '@/lib/commerce';
import {
  buildCommerceSectionRoute,
  buildCommerceShellViewModel,
  CommerceBreadcrumbHeader,
} from '@/views/commerce/catalogHierarchyShared';
import { buildTenantSelectorOptions } from '@/views/commerce/selectorOptions';

type CommissionPlanFormState = {
  name: string;
  status: string;
  currency: string;
  sellingTenantId: string;
  policyMode: string;
  calculationType: string;
  chargeType: string;
  ruleRate: string;
  ruleAmount: string;
  extraRuleEntries: CommerceMetadataEntryViewModel[];
  metadataEntries: CommerceMetadataEntryViewModel[];
};

const knownRuleFieldKeys = new Set(['mode', 'calculation']);

const automaticPolicyModes = new Set(['automatic', 'automatic_deferred']);
const percentageCalculationTypes = new Set(['percentage', 'hybrid']);
const flatCalculationTypes = new Set(['flat', 'hybrid']);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function getCalculationRules(rulesSnapshot: Record<string, unknown>): Record<string, unknown>[] {
  const calculation = rulesSnapshot.calculation;
  if (!isRecord(calculation) || !Array.isArray(calculation.rules)) {
    return [];
  }
  return calculation.rules.filter(isRecord);
}

function createEntry(overrides?: Partial<CommerceMetadataEntryViewModel>): CommerceMetadataEntryViewModel {
  return {
    id:
      overrides?.id ||
      `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`,
    key: overrides?.key || '',
    value: overrides?.value || '',
    valueType: overrides?.valueType || 'string',
    helperText: overrides?.helperText || null,
    locked: overrides?.locked || false,
  };
}

function formatPrimitiveValue(value: unknown, valueType: CommerceMetadataValueType): string {
  if (valueType === 'json') {
    return JSON.stringify(value ?? {}, null, 2);
  }
  if (valueType === 'boolean') {
    return value ? 'true' : 'false';
  }
  return value == null ? '' : String(value);
}

function inferValueType(value: unknown): CommerceMetadataValueType {
  if (typeof value === 'number') {
    return 'number';
  }
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  if (value && typeof value === 'object') {
    return 'json';
  }
  return 'string';
}

function recordToEntries(record?: Record<string, unknown> | null): CommerceMetadataEntryViewModel[] {
  return Object.entries(record || {}).map(([key, value]) => {
    const valueType = inferValueType(value);
    return createEntry({
      id: key,
      key,
      value: formatPrimitiveValue(value, valueType),
      valueType,
    });
  });
}

function parseEntryValue(entry: CommerceMetadataEntryViewModel, label: string): unknown {
  const value = entry.value.trim();
  if (entry.valueType === 'number') {
    if (!value) {
      throw new Error(`${label} "${entry.key}" must be a number.`);
    }
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      throw new Error(`${label} "${entry.key}" must be a valid number.`);
    }
    return parsed;
  }
  if (entry.valueType === 'boolean') {
    return value === 'true';
  }
  if (entry.valueType === 'json') {
    if (!value) {
      return {};
    }
    try {
      return JSON.parse(value);
    } catch {
      throw new Error(`${label} "${entry.key}" must be valid JSON.`);
    }
  }
  return entry.value;
}

function entriesToRecord(entries: CommerceMetadataEntryViewModel[], label: string): Record<string, unknown> {
  return entries.reduce<Record<string, unknown>>((acc, entry) => {
    const key = entry.key.trim();
    if (!key) {
      return acc;
    }
    acc[key] = parseEntryValue(entry, label);
    return acc;
  }, {});
}

function createEmptyForm(): CommissionPlanFormState {
  return {
    name: '',
    status: 'draft',
    currency: 'USD',
    sellingTenantId: '',
    policyMode: 'automatic',
    calculationType: 'percentage',
    chargeType: 'recurring',
    ruleRate: '',
    ruleAmount: '',
    extraRuleEntries: [],
    metadataEntries: [],
  };
}

function buildFormFromPlan(plan: CommissionPlan): CommissionPlanFormState {
  const rules = plan.rules_snapshot || {};
  const calculationRules = getCalculationRules(rules);
  const percentageRule = calculationRules.find(rule => rule.type === 'percent_of_charge_type');
  const flatRule = calculationRules.find(rule => rule.type === 'flat_amount');
  const extraRules = Object.fromEntries(
    Object.entries(rules).filter(([key]) => !knownRuleFieldKeys.has(key))
  );
  return {
    name: plan.name,
    status: plan.status,
    currency: plan.currency || 'USD',
    sellingTenantId: plan.selling_tenant_id || '',
    policyMode:
      typeof rules.mode === 'string' && ['automatic', 'automatic_deferred', 'manual'].includes(rules.mode)
        ? rules.mode
        : 'automatic',
    calculationType: percentageRule && flatRule ? 'hybrid' : flatRule ? 'flat' : 'percentage',
    chargeType:
      typeof percentageRule?.charge_type === 'string' && percentageRule.charge_type
        ? percentageRule.charge_type
        : 'recurring',
    ruleRate: percentageRule?.percent == null
      ? percentageRule?.percentage == null ? '' : String(percentageRule.percentage)
      : String(percentageRule.percent),
    ruleAmount: formatMinorCurrencyInput(
      typeof flatRule?.amount_minor === 'number' ? flatRule.amount_minor : null,
      plan.currency || 'USD',
    ),
    extraRuleEntries: recordToEntries(extraRules),
    metadataEntries: recordToEntries(plan.metadata),
  };
}

function parseOptionalNumber(value: string, label: string): number | undefined {
  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed)) {
    throw new Error(`${label} must be a valid number.`);
  }
  return parsed;
}

function buildCommissionPlanPayload(form: CommissionPlanFormState): Record<string, unknown> {
  const extraRules = entriesToRecord(form.extraRuleEntries, 'Additional rule');
  const metadata = entriesToRecord(form.metadataEntries, 'Metadata entry');
  const policyMode = form.policyMode.trim() || 'automatic';
  const rulesSnapshot: Record<string, unknown> = { ...extraRules, mode: policyMode };

  if (automaticPolicyModes.has(policyMode)) {
    const calculationRules: Record<string, unknown>[] = [];
    if (percentageCalculationTypes.has(form.calculationType)) {
      const rate = parseOptionalNumber(form.ruleRate, 'Commission rate');
      if (rate == null) {
        throw new Error('Commission rate is required for percentage calculations.');
      }
      calculationRules.push({
        type: 'percent_of_charge_type',
        charge_type: form.chargeType,
        percent: rate,
      });
    }
    if (flatCalculationTypes.has(form.calculationType)) {
      const amountMinor = parseCurrencyInputToMinor(form.ruleAmount, form.currency, 'Flat amount');
      if (amountMinor == null) {
        throw new Error('Flat amount is required for flat calculations.');
      }
      calculationRules.push({ type: 'flat_amount', amount_minor: amountMinor });
    }
    rulesSnapshot.calculation = { rules: calculationRules };
  }

  const payload: Record<string, unknown> = {
    name: form.name.trim(),
    status: form.status.trim(),
    currency: form.currency.trim() || null,
    rules_snapshot: rulesSnapshot,
    metadata,
  };
  const sellingTenantId = form.sellingTenantId.trim();
  if (sellingTenantId) {
    payload.selling_tenant_id = sellingTenantId;
  }
  return payload;
}

function countKeys(value: Record<string, unknown> | undefined): number {
  return Object.keys(value ?? {}).length;
}

function describeCommissionRule(plan: CommissionPlan): string {
  const rules = plan.rules_snapshot || {};
  const mode = typeof rules.mode === 'string' && rules.mode.trim() ? rules.mode : 'automatic';
  if (mode === 'manual') return 'Manual assignment';

  const descriptions = getCalculationRules(rules).map(rule => {
    if (rule.type === 'percent_of_charge_type') {
      const percent = rule.percent ?? rule.percentage;
      const chargeType = typeof rule.charge_type === 'string'
        ? rule.charge_type.replace(/_/g, ' ')
        : 'charges';
      return `${percent ?? 0}% of ${chargeType}`;
    }
    if (rule.type === 'flat_amount' && typeof rule.amount_minor === 'number') {
      return formatMinorCurrency(rule.amount_minor, plan.currency || 'USD');
    }
    return '';
  }).filter(Boolean);

  const policyLabel = mode === 'automatic_deferred' ? 'Approval required' : 'Automatic';
  return descriptions.length ? `${policyLabel}: ${descriptions.join(' + ')}` : policyLabel;
}

function updateEntryList(
  entries: CommerceMetadataEntryViewModel[],
  entryId: string,
  patch: Partial<CommerceMetadataEntryViewModel>
) {
  return entries.map((entry) => (entry.id === entryId ? { ...entry, ...patch } : entry));
}

function renderEntryRows(
  title: string,
  entries: CommerceMetadataEntryViewModel[],
  setEntries: React.Dispatch<React.SetStateAction<CommerceMetadataEntryViewModel[]>>,
  emptyBody: string
) {
  return (
    <VerticalStack gap={4}>
      <HorizontalStack gap={3} className="items-center justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-wider text-muted-foreground">{title}</p>
          <p className="mt-1 text-xs text-muted-foreground">{emptyBody}</p>
        </div>
        <button
          type="button"
          onClick={() => setEntries((current) => [...current, createEntry()])}
          className="inline-flex items-center gap-2 rounded-md border border-border/60 px-3 py-2 text-xs font-bold text-foreground transition-all hover:bg-muted/30"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Row
        </button>
      </HorizontalStack>
      {entries.length === 0 ? (
        <Surface className="border-dashed border-border/60 p-4 text-xs text-muted-foreground">
          No entries yet.
        </Surface>
      ) : (
        <VerticalStack gap={3}>
          {entries.map((entry) => (
            <Surface key={entry.id} className="border-border/60 p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-[1.2fr_0.7fr_1.4fr_auto]">
                <VerticalStack gap={2}>
                  <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Key</label>
                  <SilkInput
                    value={entry.key}
                    onChange={(event) =>
                      setEntries((current) => updateEntryList(current, entry.id, { key: event.target.value }))
                    }
                    disabled={entry.locked}
                  />
                </VerticalStack>
                <VerticalStack gap={2}>
                  <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Type</label>
                  <SilkSelect
                    value={entry.valueType}
                    onValueChange={(value) =>
                      setEntries((current) =>
                        updateEntryList(current, entry.id, {
                          valueType: value as CommerceMetadataValueType,
                          value:
                            value === 'boolean'
                              ? 'false'
                              : entry.valueType === 'boolean'
                                ? ''
                                : entry.value,
                        })
                      )
                    }
                  >
                    <option value="string">String</option>
                    <option value="number">Number</option>
                    <option value="boolean">Boolean</option>
                    <option value="json">JSON</option>
                  </SilkSelect>
                </VerticalStack>
                <VerticalStack gap={2}>
                  <label className="text-[10px] font-black uppercase tracking-wider text-muted-foreground">Value</label>
                  {entry.valueType === 'boolean' ? (
                    <SilkSelect
                      value={entry.value === 'true' ? 'true' : 'false'}
                      onValueChange={(value) =>
                        setEntries((current) => updateEntryList(current, entry.id, { value }))
                      }
                    >
                      <option value="false">False</option>
                      <option value="true">True</option>
                    </SilkSelect>
                  ) : entry.valueType === 'json' ? (
                    <textarea
                      value={entry.value}
                      onChange={(event) =>
                        setEntries((current) => updateEntryList(current, entry.id, { value: event.target.value }))
                      }
                      rows={4}
                      className="w-full rounded-md border border-border/60 bg-background px-3 py-2 font-mono text-xs text-foreground outline-none transition-all focus:border-primary/40"
                    />
                  ) : (
                    <SilkInput
                      value={entry.value}
                      onChange={(event) =>
                        setEntries((current) => updateEntryList(current, entry.id, { value: event.target.value }))
                      }
                    />
                  )}
                </VerticalStack>
                <div className="flex items-end justify-end">
                  <button
                    type="button"
                    onClick={() => setEntries((current) => current.filter((item) => item.id !== entry.id))}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-border/60 text-muted-foreground transition-all hover:bg-muted/30 hover:text-foreground"
                    aria-label={`Remove ${title} row`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            </Surface>
          ))}
        </VerticalStack>
      )}
    </VerticalStack>
  );
}

export default function CommissionPlansPage() {
  const { activeTenant, organizations } = useScope();
  const navigation = useNavigation();
  const [plans, setPlans] = useState<CommissionPlan[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [accessDeniedStatus, setAccessDeniedStatus] = useState<number | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTab, setEditorTab] = useState<string>('general');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const query = useCommerceRegistryQuery({
    facetKeys: ['status'],
    facetValues: { status: ['active', 'archived', 'draft', 'inactive'] },
    sortFields: COMMISSION_PLAN_SORT_FIELDS,
  });
  const searchQuery = query.search;
  const sorting = useMemo<SortingState>(() => query.sort.map(token => ({
    id: token.startsWith('-') ? token.slice(1) : token,
    desc: token.startsWith('-'),
  })), [query.sort]);
  const [totalPlans, setTotalPlans] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const requestSequence = useRef(0);
  const [form, setForm] = useState<CommissionPlanFormState>(createEmptyForm());
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const columnFilters = useMemo<ColumnFiltersState>(() => (
    query.facets.status.length ? [{ id: 'status', value: query.facets.status }] : []
  ), [query.facets.status]);

  const loadData = async () => {
    const requestId = ++requestSequence.current;
    if (!activeTenant?.id) {
      return;
    }
    setLoading(true);
    setError(null);
    setAccessDeniedStatus(null);
    try {
      const data = await listCommissionPlans({
        catalogOwnerTenantId: activeTenant.id,
        search: searchQuery || undefined,
        status: query.facets.status,
        sort: query.sort,
        include_facets: ['status'],
        ...toLimitOffset(query.pagination),
      });
      if (requestId !== requestSequence.current) return;
      const nextPageIndex = lastPageIndexForTotal(data.total_count, query.pagination.pageSize);
      if (query.pagination.pageIndex > nextPageIndex) {
        query.onPaginationChange(current => ({ ...current, pageIndex: nextPageIndex }));
        return;
      }
      setPlans(data.items);
      setTotalPlans(data.total_count);
      setStatusCounts(Object.fromEntries((data.facets.status ?? []).map(bucket => [bucket.value, bucket.count])));
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        setError(getCommerceErrorMessage(err, 'Failed to load commission plans.'));
      }
      if (requestId === requestSequence.current) setPlans([]);
    } finally {
      if (requestId === requestSequence.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (!activeTenant?.id) {
      setPlans([]);
      setTotalPlans(0);
      setStatusCounts({});
      setLoading(false);
      return;
    }
    void loadData();
  }, [activeTenant?.id, query.facets.status, query.pagination, query.sort, searchQuery]);

  const sellingTenantOptions = useMemo(
    () =>
      buildTenantSelectorOptions({
        activeTenant,
        organizations,
        observedTenantIds: plans
          .map(plan => plan.selling_tenant_id?.trim() || '')
          .filter(Boolean),
      }),
    [activeTenant, organizations, plans],
  );

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const payload = buildCommissionPlanPayload(form);
      if (editingId) {
        await updateCommissionPlan(editingId, payload);
      } else {
        await createCommissionPlan(payload);
      }
      await loadData();
      setEditorOpen(false);
    } catch (err) {
      setError(getCommerceErrorMessage(err, 'Failed to save commission plan.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (plan: CommissionPlan) => {
    if (!window.confirm(`Archive commission plan "${plan.name}"?`)) {
      return;
    }

    setError(null);
    try {
      await deleteCommissionPlan(plan.commission_plan_id);
      await loadData();
      toast.success('Commission plan archived', {
        description: plan.name,
      });
      if (editingId === plan.commission_plan_id) {
        setEditorOpen(false);
        setEditingId(null);
      }
    } catch (err) {
      const status = getCommerceErrorStatus(err);
      if (status === 401 || status === 403) {
        setAccessDeniedStatus(status);
      } else {
        const message = getCommerceErrorMessage(err, 'Failed to archive commission plan.');
        setError(message);
        toast.error('Failed to archive commission plan', { description: message });
      }
    }
  };

  const shellViewModel = buildCommerceShellViewModel({
    activeTenant,
    accessDeniedStatus,
    activeSectionId: 'commission_plans',
    searchQuery,
    searchPlaceholder: 'Search commission plans...',
    onSearchChange: query.setSearch,
    error,
    blockingReason: 'Your current role does not have permission to manage commission plans.',
    onSelectSection: (sectionId) => navigation.navigate(buildCommerceSectionRoute(sectionId)),
    primaryAction: {
      id: 'create-plan',
      label: 'New Plan',
      onAction: () => {
        setEditingId(null);
        setForm(createEmptyForm());
        setEditorOpen(true);
      },
    },
  });

  const EDITOR_TABS: WizardTab[] = [
    { id: 'general', label: 'General', icon: <Target className="h-4 w-4" /> },
    { id: 'strategy', label: 'Strategy', icon: <Activity className="h-4 w-4" /> },
    { id: 'advanced', label: 'Advanced', icon: <Settings2 className="h-4 w-4" /> },
  ];

  const columns = useMemo<ColumnDef<CommissionPlan>[]>(() => [
    {
      id: 'select',
      size: 40,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'name',
      size: 300,
      header: 'Name',
      cell: ({ row }) => {
        const plan = row.original;
        return (
          <button
            type="button"
            onClick={() => {
              setEditingId(plan.commission_plan_id);
              setForm(buildFormFromPlan(plan));
              setEditorOpen(true);
            }}
            className="group min-w-0 text-left"
          >
            <p className="text-sm font-black text-foreground transition-colors group-hover:text-primary">
              {plan.name}
            </p>
            <DescriptionText className="line-clamp-1 text-[11px]">
              {describeCommissionRule(plan)}
            </DescriptionText>
          </button>
        );
      },
    },
    {
      accessorKey: 'status',
      size: 100,
      header: 'Status',
      cell: ({ row }) => (
        <RegistryStatus label={row.original.status} variant={row.original.status === 'active' ? 'success' : 'muted'} />
      ),
      filterFn: (row, id, value) => {
        return value.includes(row.getValue(id));
      },
    },
    {
      accessorKey: 'currency',
      size: 80,
      header: 'Currency',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">{row.original.currency || 'USD'}</span>
      ),
    },
    {
      accessorKey: 'selling_tenant_id',
      size: 150,
      header: 'Scope',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {row.original.selling_tenant_id || 'All resellers'}
        </span>
      ),
    },
    {
      accessorKey: 'updated_at',
      size: 120,
      header: 'Updated',
      cell: ({ row }) => (
        <span className="text-[11px] font-bold text-foreground/80">
          {new Date(row.original.updated_at).toLocaleDateString()}
        </span>
      ),
    },
    {
      id: 'actions',
      enableSorting: false,
      size: 80,
      header: () => <div className="text-right">Actions</div>,
      cell: ({ row }) => {
        const plan = row.original;
        return (
          <HorizontalStack gap={2} className="justify-end shrink-0">
            <button
              type="button"
              onClick={() => {
                setEditingId(plan.commission_plan_id);
                setForm(buildFormFromPlan(plan));
                setEditorOpen(true);
              }}
              aria-label={`Edit ${plan.name}`}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-primary/40 hover:text-primary hover:bg-primary/5"
            >
              <Pencil className="h-3 w-3" />
            </button>
            <button
              type="button"
              onClick={() => void handleDelete(plan)}
              aria-label={`Archive ${plan.name}`}
              className="flex h-7 w-7 items-center justify-center rounded-md border border-border/40 text-muted-foreground/50 transition-all hover:border-rose-400/50 hover:text-rose-600 hover:bg-rose-500/5"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          </HorizontalStack>
        );
      },
    },
  ], [editingId, loadData]);
  defineCommerceRegistryColumns(columns, { mode: 'manual', sortFields: COMMISSION_PLAN_SORT_FIELDS, registry: 'commission-plans' });

  const summaryItems = [
    { label: 'Status', value: form.status.toUpperCase(), complete: true },
    { label: 'Policy', value: form.policyMode.replace(/_/g, ' ').toUpperCase(), complete: true },
    { label: 'Currency', value: form.currency || 'USD', complete: true },
  ];

  const editorWorkspace = (
    <WizardWorkspace
      isOpen={editorOpen}
      isSaving={saving}
      title={editingId ? 'Edit Commission Plan' : 'New Commission Plan'}
      subtitle="Author the operational payout strategy first, then add any extra rule keys the backend should preserve."
      tabs={EDITOR_TABS}
      activeTabId={editorTab}
      onTabChange={setEditorTab}
      saveLabel="Save Plan"
      onSave={handleSave}
      onClose={() => setEditorOpen(false)}
      summaryItems={summaryItems}
    >
      <VerticalStack gap={8}>
        {editorTab === 'general' && (
          <section>
            <SectionHeading title="Identity & Scope" subtitle="Define the name and boundaries of this plan." />
            <Surface className="mt-4 border-border/60 p-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <Field label="Plan Name">
                  <input
                    value={form.name}
                    onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                    className={inputStyles}
                  />
                </Field>
                <Field label="Selling Tenant" description="Leave blank for shared/default scope">
                  <CommerceObjectSelector
                    options={sellingTenantOptions}
                    value={form.sellingTenantId}
                    onChange={sellingTenantId => setForm((current) => ({ ...current, sellingTenantId }))}
                    placeholder="Search selling tenants..."
                    emptyMessage="No tenant overrides are available yet."
                  />
                </Field>
                <Field label="Currency">
                  <input
                    value={form.currency}
                    onChange={(event) => setForm((current) => ({ ...current, currency: event.target.value }))}
                    className={inputStyles}
                  />
                </Field>
                <Field label="Status">
                  <select
                    value={form.status}
                    onChange={(event) => setForm((current) => ({ ...current, status: event.target.value }))}
                    className={inputStyles}
                  >
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                  </select>
                </Field>
              </div>
            </Surface>
          </section>
        )}

        {editorTab === 'strategy' && (
          <section>
            <SectionHeading title="Payout Strategy" subtitle="Define how and when commission is calculated." />
            <Surface className="mt-4 border-border/60 p-6">
              <VerticalStack gap={4}>
                <Surface className="border-border/60 bg-muted/20 p-4">
                  <PropertyGrid cols={4}>
                    <PropertyItem label="Policy" value={form.policyMode.replace(/_/g, ' ')} />
                    <PropertyItem
                      label="Calculation"
                      value={form.policyMode === 'manual' ? 'Manual assignment' : form.calculationType}
                    />
                    <PropertyItem
                      label="Rate"
                      value={form.policyMode !== 'manual' && percentageCalculationTypes.has(form.calculationType) && form.ruleRate
                        ? `${form.ruleRate}%`
                        : 'Not set'}
                    />
                    <PropertyItem
                      label="Flat Amount"
                      value={form.policyMode !== 'manual' && flatCalculationTypes.has(form.calculationType) && form.ruleAmount
                        ? `${form.currency} ${form.ruleAmount}`
                        : 'Not set'}
                    />
                  </PropertyGrid>
                </Surface>
                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                  <Field
                    label="Policy Mode"
                    description="Choose when the calculated commission becomes a ledger entry."
                  >
                    <select
                      value={form.policyMode}
                      onChange={(event) => setForm((current) => ({ ...current, policyMode: event.target.value }))}
                      className={inputStyles}
                    >
                      <option value="automatic">Automatic</option>
                      <option value="automatic_deferred">Automatic — Approval Required</option>
                      <option value="manual">Manual Assignment</option>
                    </select>
                  </Field>
                  {form.policyMode !== 'manual' && (
                    <Field label="Calculation Type">
                      <select
                        value={form.calculationType}
                        onChange={(event) => setForm((current) => ({ ...current, calculationType: event.target.value }))}
                        className={inputStyles}
                      >
                        <option value="percentage">Percentage of Charge Type</option>
                        <option value="flat">Flat Amount</option>
                        <option value="hybrid">Percentage Plus Flat Amount</option>
                      </select>
                    </Field>
                  )}
                  {form.policyMode !== 'manual' && percentageCalculationTypes.has(form.calculationType) && (
                    <>
                      <Field label="Charge Type">
                        <select
                          value={form.chargeType}
                          onChange={(event) => setForm((current) => ({ ...current, chargeType: event.target.value }))}
                          className={inputStyles}
                        >
                          <option value="recurring">Recurring</option>
                          <option value="one_time">One Time</option>
                          <option value="installation">Installation</option>
                          <option value="deposit">Deposit</option>
                          <option value="usage">Usage</option>
                          <option value="credit">Credit</option>
                        </select>
                      </Field>
                      <Field label="Rate (%)">
                        <PercentageInput
                          value={form.ruleRate}
                          onChange={(event) => setForm((current) => ({ ...current, ruleRate: event.target.value }))}
                          placeholder="12.5"
                        />
                      </Field>
                    </>
                  )}
                  {form.policyMode !== 'manual' && flatCalculationTypes.has(form.calculationType) && (
                    <Field label="Flat Amount">
                      <CurrencyInput
                        value={form.ruleAmount}
                        onChange={(event) => setForm((current) => ({ ...current, ruleAmount: event.target.value }))}
                        placeholder="25.00"
                      />
                    </Field>
                  )}
                </div>
              </VerticalStack>
            </Surface>
          </section>
        )}

        {editorTab === 'advanced' && (
          <VerticalStack gap={8}>
            <section>
              <SectionHeading title="Additional Rule Fields" subtitle="Use this section for any rule keys the payout strategy fields do not cover." />
              <Surface className="mt-4 border-border/60 p-6">
                {renderEntryRows(
                  'Additional Rule Fields',
                  form.extraRuleEntries,
                  (updater) =>
                    setForm((current) => ({
                      ...current,
                      extraRuleEntries:
                        typeof updater === 'function' ? updater(current.extraRuleEntries) : updater,
                    })),
                  'Use this section for any rule keys the payout strategy fields do not cover.'
                )}
              </Surface>
            </section>
            
            <section>
              <SectionHeading title="Metadata" subtitle="Capture operational annotations without forcing raw JSON editing." />
              <Surface className="mt-4 border-border/60 p-6">
                {renderEntryRows(
                  'Metadata',
                  form.metadataEntries,
                  (updater) =>
                    setForm((current) => ({
                      ...current,
                      metadataEntries:
                        typeof updater === 'function' ? updater(current.metadataEntries) : updater,
                    })),
                  'Capture operational annotations without forcing raw JSON editing.'
                )}
              </Surface>
            </section>
          </VerticalStack>
        )}
      </VerticalStack>
    </WizardWorkspace>
  );

  return (
    <div className="flex h-full flex-col">
      <CommerceCatalogWorkspace
        viewModel={shellViewModel}
        padded={false}
        takeoverActive={editorOpen}
        replaceShellHeaderWhenTakeoverActive
        takeoverContent={editorWorkspace}
        titleContent={
          <CommerceBreadcrumbHeader
            breadcrumbs={[{ label: 'Commission Plans' }]}
            title="Commission Plans"
            description="Define reseller and agent payout plans with explicit scope and durable commission rules."
          />
        }
      >
        {!editorOpen && (
          <RegistryWorkspace>
            <RegistryTableSurface>
              <DataTable
                  mode="manual"
                  columns={columns}
                  data={plans}
                  sorting={sorting}
                  onSortingChange={updater => {
                    const next = typeof updater === 'function' ? updater(sorting) : updater;
                    query.setSort(next.map(item => `${item.desc ? '-' : ''}${item.id}`));
                  }}
                  globalFilter={searchQuery}
                  columnFilters={columnFilters}
                  onColumnFiltersChange={updater => {
                    const next = typeof updater === 'function' ? updater(columnFilters) : updater;
                    const filter = next.find(item => item.id === 'status');
                    query.setFacet('status', Array.isArray(filter?.value) ? filter.value.map(String) : []);
                  }}
                  rowSelection={rowSelection}
                  onRowSelectionChange={setRowSelection}
                  pagination={query.pagination}
                  onPaginationChange={query.onPaginationChange}
                  manualPagination
                  totalRowCount={totalPlans}
                  loading={loading}
                  className="border-none rounded-none"
                  paginationClassName="px-0 py-0"
                  showPagination
                  emptyState={
                    <div className="px-0 py-12 text-center">
                      <p className="text-sm font-black text-foreground">No Commission Plans</p>
                      <DescriptionText className="mt-1 text-xs">
                        {searchQuery || query.facets.status.length
                          ? 'No commission plans matched the current search or filters.'
                          : 'Create a commission plan to govern reseller and agent payouts.'}
                      </DescriptionText>
                      {searchQuery || query.facets.status.length ? (
                        <button type="button" onClick={query.clear} className="mt-3 text-xs font-semibold text-primary hover:underline">
                          Reset search and filters
                        </button>
                      ) : null}
                    </div>
                  }
                  renderToolbar={table => (
                    <DataTableToolbar
                      table={table}
                      searchValue={searchQuery}
                      onSearchChange={query.setSearch}
                      searchPlaceholder="Search all commission plans..."
                      resultCount={totalPlans}
                      className="border-b border-border/40 bg-transparent px-0 py-0"
                      filters={
                        <DataTableFacetedFilter
                          title="Status"
                          selectedValues={new Set(query.facets.status)}
                          onFilterChange={values => query.setFacet('status', Array.from(values))}
                          counts={statusCounts}
                          options={['active', 'draft', 'archived', 'inactive'].map(value => ({
                            label: value.charAt(0).toUpperCase() + value.slice(1),
                            value,
                          }))}
                        />
                      }
                    />
                  )}
                />
            </RegistryTableSurface>
          </RegistryWorkspace>
        )}
      </CommerceCatalogWorkspace>
    </div>
  );
}
